import React, { forwardRef, useMemo } from 'react'
import './tooltip.style.scss'
import { CPopover } from '@coreui/react-pro'
import PropTypes from 'prop-types'
import { useTheme } from 'src/components/ThemeContext'

const Tooltip = forwardRef(
  (
    {
      content,
      placement,
      children,
      childClassName,
      darkText,
      tooltipStyleClass,
      tooltipWidthClass,
      isTooltipSameAsTheme,
      isOpen,
      trigger: triggerProp,
      ...rest
    },
    ref,
  ) => {
    const { mode } = useTheme()

    const tooltipClass = useMemo(() => {
      return isTooltipSameAsTheme
        ? `${mode === 'dark' ? 'tooltip-dark' : 'tooltip-light'}`
        : `${mode === 'dark' ? 'tooltip-light' : 'tooltip-dark'} ${darkText ? 'dark-text' : ''}`
    }, [isTooltipSameAsTheme, mode, darkText])

    // Forced closed: no hover/click triggers so mouseenter cannot reopen during exit animation / navigation
    const cPopoverTrigger =
      isOpen === false ? [] : isOpen !== undefined ? triggerProp ?? [] : triggerProp ?? 'hover'

    return (
      <>
        {content ? (
          <CPopover
            content={
              <div
                className={`popover-body ${darkText ? 'dark-text' : ''} tooltip-default-style 
            ${tooltipStyleClass || ''} ${tooltipWidthClass || 'tooltip-default-width'} `}
              >
                {content}
              </div>
            }
            placement={placement}
            trigger={cPopoverTrigger}
            visible={isOpen}
            className={`popover-tooltip ${tooltipClass} ${
              isTooltipSameAsTheme ? 'custom-tooltip-theme' : ''
            }`}
            popperConfig={
              placement === 'top-start' || placement === 'bottom-start'
                ? {
                    modifiers: [{ name: 'arrow', options: { element: '[data-popper-arrow]' } }],
                  }
                : {}
            }
            {...rest}
          >
            {/* Forward ref to children (Button) */}
            <div ref={ref} className={childClassName}>
              {children}
            </div>
          </CPopover>
        ) : (
          <div ref={ref} className={childClassName}>
            {children}
          </div>
        )}
      </>
    )
  },
)
Tooltip.displayName = 'Tooltip'
Tooltip.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placement: PropTypes.string,
  children: PropTypes.node.isRequired,
  childClassName: PropTypes.string,
  darkText: PropTypes.bool,
  tooltipStyleClass: PropTypes.string,
  tooltipWidthClass: PropTypes.string,
  isTooltipSameAsTheme: PropTypes.bool,
  isOpen: PropTypes.bool, // use this for showing tooltip on click
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
}

Tooltip.defaultProps = {
  content: 'Enter tooltip content',
  placement: 'auto',
  darkText: false,
  isTooltipSameAsTheme: false,
  isOpen: undefined,
  trigger: undefined,
}

export default Tooltip
